import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const HelpSupport = () => {
    const { user } = useAuth();
    const [openFaq, setOpenFaq] = useState(null);
    const [zoomedImage, setZoomedImage] = useState(null);
    const [isContactModalOpen, setIsContactModalOpen] = useState(false);
    const [contactForm, setContactForm] = useState({
        email: user?.email || '',
        subject: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [contactSuccess, setContactSuccess] = useState(false);
    const [contactError, setContactError] = useState('');

    useEffect(() => {
        if (window._paq) {
            window._paq.push(['setCustomUrl', window.location.pathname]);
            window._paq.push(['setDocumentTitle', 'Aide & Support']);
            window._paq.push(['trackPageView']);
        }
    }, []);

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setContactError('');
        setContactSuccess(false);

        try {
            const res = await fetch('/api/support/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(contactForm)
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'Erreur lors de l\'envoi du message');
            }

            setContactSuccess(true);
            setContactForm({ ...contactForm, subject: '', message: '' });
            setTimeout(() => {
                setIsContactModalOpen(false);
                setContactSuccess(false);
            }, 3000);
        } catch (err) {
            setContactError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const faqItems = [
        {
            q: "Comment fonctionne la recherche dans le plan d'entrepôt ?",
            a: "Dans la vue 'Plan Dynamique', utilisez la barre de recherche pour localiser un produit. Son emplacement (allée/rack) clignotera sur la carte pour vous guider visuellement."
        },
        {
            q: "Comment utiliser le scanner sur mobile ?",
            a: "Connectez-vous sur votre smartphone, ouvrez le menu 'Scanner'. Vous pouvez scanner un code-barres pour voir les détails d'un produit ou enregistrer une entrée/sortie rapide."
        },
        {
            q: "C'est quoi une 'Commande Intelligente' ?",
            a: "C'est une suggestion d'achat générée automatiquement basée sur vos niveaux de stock actuels, vos seuils d'alerte et vos fournisseurs habituels. Elle vous permet de créer un bon de commande en un clic."
        },
        {
            q: "Peut-on gérer plusieurs catalogues par fournisseur ?",
            a: "Oui, chaque fournisseur peut avoir sa propre liste de produits avec des prix et des SKU spécifiques. Vous pouvez consulter ces catalogues dans la fiche détaillée du fournisseur."
        },
        {
            q: "Que faire en cas d'erreur lors d'un import ?",
            a: "Vérifiez que votre fichier Excel suit bien le modèle fourni (téléchargeable dans la fenêtre d'import). Les causes courantes sont des SKU en double ou des catégories inexistantes."
        },
        {
            q: "Où puis-je activer le mode multi-sites ou changer la devise ?",
            a: "Toutes les configurations globales se trouvent dans le menu 'Paramètres', accessible uniquement aux utilisateurs ayant les droits d'administration."
        }
    ];

    const guideSteps = [
        {
            title: "Organisation",
            desc: "Cartographiez vos entrepôts et gérez vos zones de stockage facilement.",
            icon: "map",
            color: "text-blue-500",
            bg: "bg-blue-500/10"
        },
        {
            title: "Approvisionnement",
            desc: "Pilotez vos fournisseurs et automatisez vos commandes avec les commandes intelligentes.",
            icon: "shopping_cart_checkout",
            color: "text-emerald-500",
            bg: "bg-emerald-500/10"
        },
        {
            title: "Optimisation",
            desc: "Utilisez les analyses pour identifier les ruptures de stock et les surstocks.",
            icon: "trending_up",
            color: "text-primary",
            bg: "bg-primary/10"
        }
    ];

    return (
        <div className="max-w-6xl mx-auto space-y-12 pb-20 animate-in fade-in duration-500">
            {/* Header section with glassmorphism effect */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 border-b border-slate-200/50 dark:border-slate-800/50">
                <div className="flex flex-col gap-1">
                    <h1 className="text-4xl font-black tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Les principales fonctionnalités</h1>
                    <p className="text-slate-500 dark:text-slate-400 text-lg">Tout ce dont vous avez besoin pour maîtriser vos stocks</p>
                </div>
            </div>

            {/* Quick Guide Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {guideSteps.map((step, idx) => (
                    <div key={idx} className="group p-8 bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 hover:border-primary/50 transition-all hover:shadow-xl hover:shadow-primary/5 cursor-default">
                        <div className={`size-14 rounded-xl ${step.bg} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                            <span className={`material-symbols-outlined text-3xl ${step.color}`}>{step.icon}</span>
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">{step.title}</h3>
                        <p className="text-slate-500 dark:text-slate-400 leading-relaxed font-medium">{step.desc}</p>
                    </div>
                ))}
            </div>

            {/* Features Spotlight */}
            <div className="space-y-16">
                <div className="flex items-center gap-4">
                    <div className="h-px flex-1 bg-slate-200 dark:border-slate-800"></div>
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-widest text-sm text-center">Fonctionnalités Clés</h2>
                    <div className="h-px flex-1 bg-slate-200 dark:border-slate-800"></div>
                </div>

                {/* Section 1: Inventaire & Scan */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="space-y-6">
                        <div className="inline-flex items-center px-4 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-black uppercase tracking-wider">
                            Gestion Quotidienne
                        </div>
                        <h3 className="text-3xl font-black text-slate-900 dark:text-white">Inventaire & Mobilité</h3>
                        <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                            Visualisez vos stocks en temps réel et utilisez n'importe quel appareil mobile pour scanner vos QR Codes.
                            Idéal pour les inventaires tournants et les réceptions de marchandises ultra-rapides.
                        </p>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                            {['Mise à jour en direct', 'Scan QR & Barcode', 'Import/Export Excel', 'Alertes Critiques'].map((item, i) => (
                                <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-slate-300 font-bold">
                                    <span className="material-symbols-outlined text-emerald-500 font-black">check_circle</span>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="relative group">
                        <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                        <div className="relative rounded-2xl overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl transform group-hover:-rotate-1 transition-transform duration-500">
                            <img
                                src="/img/help/inventory.webp"
                                alt="Aperçu Inventaire"
                                className="w-full aspect-video object-cover cursor-zoom-in"
                                onClick={() => setZoomedImage('/img/help/inventory.webp')}
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                </div>

                {/* Section 2: Mobilité & Scanner */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pt-8">
                    <div className="relative order-2 lg:order-1 group">
                        <div className="absolute inset-0 bg-orange-500/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                        <div className="relative rounded-2xl overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl transform group-hover:rotate-1 transition-transform duration-500">
                            <img
                                src="/img/help/scanner.webp"
                                alt="Scan & Mobile"
                                className="w-full aspect-video object-cover cursor-zoom-in"
                                onClick={() => setZoomedImage('/img/help/scanner.webp')}
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                    <div className="space-y-6 order-1 lg:order-2">
                        <div className="inline-flex items-center px-4 py-1.5 bg-orange-100 text-orange-700 rounded-full text-xs font-black uppercase tracking-wider">
                            Mobilité
                        </div>
                        <h3 className="text-3xl font-black text-slate-900 dark:text-white">Le Scanner dans votre poche</h3>
                        <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                            Éliminez les erreurs de saisie manuelle. Utilisez n'importe quel smartphone avec l'application pour scanner vos QR Codes et code-barres. Idéal pour être efficace sur le terrain de votre entrepôt !
                        </p>
                    </div>
                </div>

                {/* Section 3: Plans Dynamiques */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pt-8">
                    <div className="relative order-2 lg:order-1 group">
                        <div className="absolute inset-0 bg-blue-500/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                        <div className="relative rounded-2xl overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl transform group-hover:rotate-1 transition-transform duration-500">
                            <img
                                src="/img/help/warehouse_map.webp"
                                alt="Plan Dynamique"
                                className="w-full aspect-video object-cover cursor-zoom-in"
                                onClick={() => setZoomedImage('/img/help/warehouse_map.webp')}
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                    <div className="space-y-6 order-1 lg:order-2">
                        <div className="inline-flex items-center px-4 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-black uppercase tracking-wider">
                            Logistique
                        </div>
                        <h3 className="text-3xl font-black text-slate-900 dark:text-white">Plan Dynamique & Recherche</h3>
                        <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                            Ne perdez plus de temps à chercher vos colis. Notre moteur de recherche spatial vous permet de localiser instantanément n'importe quel produit sur le plan de l'entrepôt.
                        </p>
                        <div className="flex flex-col gap-4">
                            <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
                                <span className="font-bold text-slate-900 dark:text-white block mb-1">Localisation visuelle</span>
                                <span className="text-sm text-slate-500">Les zones de stockage s'illuminent sur le plan lors d'une recherche.</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 4: Fournisseurs & Smart Orders */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pt-8">
                    <div className="space-y-6">
                        <div className="inline-flex items-center px-4 py-1.5 bg-purple-100 text-purple-700 rounded-full text-xs font-black uppercase tracking-wider">
                            Achats
                        </div>
                        <h3 className="text-3xl font-black text-slate-900 dark:text-white">Fournisseurs & Commandes intelligentes</h3>
                        <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                            Gérez vos fournisseurs et leurs catalogues produits. Le système analyse vos besoins et suggère des commandes intelligentes pour éviter les ruptures sans immobiliser de trésorerie.
                        </p>
                    </div>
                    <div className="relative group">
                        <div className="absolute inset-0 bg-purple-500/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                        <div className="relative rounded-2xl overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl transform group-hover:-rotate-1 transition-transform duration-500">
                            <img
                                src="/img/help/smart_order.webp"
                                alt="Smart Orders"
                                className="w-full aspect-video object-cover cursor-zoom-in"
                                onClick={() => setZoomedImage('/img/help/smart_order.webp')}
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                </div>

                {/* Section 5: Analyses & Suivi */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pt-8">
                    <div className="relative order-2 lg:order-1 group">
                        <div className="absolute inset-0 bg-teal-500/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                        <div className="relative rounded-2xl overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl transform group-hover:rotate-1 transition-transform duration-500">
                            <img
                                src="/img/help/analytics.webp"
                                alt="Analytiques"
                                className="w-full aspect-video object-cover object-left-top cursor-zoom-in"
                                onClick={() => setZoomedImage('/img/help/analytics.webp')}
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                    <div className="space-y-6 order-1 lg:order-2">
                        <div className="inline-flex items-center px-4 py-1.5 bg-teal-100 text-teal-700 rounded-full text-xs font-black uppercase tracking-wider">
                            Performance
                        </div>
                        <h3 className="text-3xl font-black text-slate-900 dark:text-white">Analyses & Tableau de bord</h3>
                        <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                            Pilotez votre activité avec des indicateurs de performance clés (KPI). Suivez la rotation des stocks, identifiez les produits dormants et analysez vos coûts d'un seul coup d'œil.
                        </p>
                    </div>
                </div>

                {/* Section 6: Administration & Sécurité */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center pt-8">
                    <div className="space-y-6">
                        <div className="inline-flex items-center px-4 py-1.5 bg-rose-100 text-rose-700 rounded-full text-xs font-black uppercase tracking-wider">
                            Sécurité
                        </div>
                        <h3 className="text-3xl font-black text-slate-900 dark:text-white">Administration & Rôles</h3>
                        <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed">
                            Gérez vos utilisateurs et sécurisez l'accès à vos données grâce au système de rôles granulaires (RBAC). Contrôlez qui peut voir, modifier ou valider les opérations dans chaque module.
                        </p>
                    </div>
                    <div className="relative group">
                        <div className="absolute inset-0 bg-rose-500/20 blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                        <div className="relative rounded-2xl overflow-hidden border-4 border-white dark:border-slate-800 shadow-2xl transform group-hover:-rotate-1 transition-transform duration-500">
                            <img
                                src="/img/help/admin.webp"
                                alt="Administration"
                                className="w-full aspect-video object-cover object-left-top cursor-zoom-in"
                                onClick={() => setZoomedImage('/img/help/admin.webp')}
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-slate-50 dark:bg-slate-800/30 rounded-3xl p-8 lg:p-12 border border-slate-200 dark:border-slate-700">
                <div className="max-w-3xl mx-auto space-y-8">
                    <div className="text-center space-y-2">
                        <h2 className="text-3xl font-black text-slate-900 dark:text-white">Questions Fréquentes</h2>
                        <p className="text-slate-500 dark:text-slate-400">Trouvez rapidement les réponses à vos interrogations</p>
                    </div>

                    <div className="space-y-3">
                        {faqItems.map((item, idx) => (
                            <div key={idx} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm hover:border-primary/30 transition-colors">
                                <button
                                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                    className="w-full px-6 py-5 flex items-center justify-between text-left group"
                                >
                                    <span className="font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors">{item.q}</span>
                                    <span className={`material-symbols-outlined transition-transform duration-300 ${openFaq === idx ? 'rotate-180 text-primary' : 'text-slate-400'}`}>
                                        expand_more
                                    </span>
                                </button>
                                <div className={`px-6 transition-all duration-300 ease-in-out ${openFaq === idx ? 'max-h-40 pb-5 opacity-100' : 'max-h-0 opacity-0'}`}>
                                    <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                                        {item.a}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Support CTA */}
            <div className="relative overflow-hidden bg-primary rounded-3xl p-12 text-center text-white shadow-2xl shadow-primary/20 group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32 transition-transform duration-1000 group-hover:scale-150"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 rounded-full blur-3xl -ml-32 -mb-32"></div>

                <div className="relative z-10 max-w-2xl mx-auto space-y-6">
                    <span className="material-symbols-outlined text-6xl opacity-80">support_agent</span>
                    <h2 className="text-4xl font-black tracking-tight">Besoin d'un accompagnement sur mesure ?</h2>
                    <p className="text-primary-foreground/80 text-lg font-bold">
                        Nous sommes à votre disposition pour vous aider dans l'intégration de Stockizio
                        ou pour répondre à vos demandes spécifiques.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4 pt-4">
                        <button
                            onClick={() => setIsContactModalOpen(true)}
                            className="px-8 py-4 bg-white text-primary rounded-xl font-black hover:bg-slate-50 transition-all shadow-xl shadow-black/10"
                        >
                            Contacter nous !
                        </button>
                        {/* <button className="px-8 py-4 bg-primary-dark/30 hover:bg-primary-dark/40 border border-white/20 rounded-xl font-black transition-all">
                            Documentation API
                        </button> */}
                    </div>
                </div>
            </div>

            {/* Modal Contact */}
            {isContactModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800 flex flex-col">
                        <div className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
                            <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-primary">mail</span>
                                Nous contacter
                            </h3>
                            <button
                                onClick={() => setIsContactModalOpen(false)}
                                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700"
                            >
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                        <div className="p-6">
                            {contactSuccess ? (
                                <div className="text-center space-y-4 py-8">
                                    <div className="size-16 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <span className="material-symbols-outlined text-4xl">check_circle</span>
                                    </div>
                                    <h4 className="text-xl font-bold text-slate-900 dark:text-white">Message envoyé !</h4>
                                    <p className="text-slate-500 dark:text-slate-400">Notre équipe reviendra vers vous très rapidement.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleContactSubmit} className="space-y-4">
                                    {contactError && (
                                        <div className="p-4 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded-xl text-sm font-medium flex gap-3 items-center">
                                            <span className="material-symbols-outlined">error</span>
                                            {contactError}
                                        </div>
                                    )}
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Votre adresse e-mail</label>
                                        <input
                                            type="email"
                                            required
                                            value={contactForm.email}
                                            onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary outline-none transition-shadow text-slate-900 dark:text-white shadow-sm"
                                            placeholder="vous@exemple.com"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Sujet</label>
                                        <input
                                            type="text"
                                            required
                                            value={contactForm.subject}
                                            onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary outline-none transition-shadow text-slate-900 dark:text-white shadow-sm"
                                            placeholder="Comment pouvons-nous vous aider ?"
                                        />
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Message</label>
                                        <textarea
                                            required
                                            rows="5"
                                            value={contactForm.message}
                                            onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-none rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary outline-none transition-shadow text-slate-900 dark:text-white shadow-sm resize-none custom-scrollbar"
                                            placeholder="Détaillez votre demande ici..."
                                        />
                                    </div>
                                    <div className="pt-2">
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full py-3 px-4 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-primary/20"
                                        >
                                            {isSubmitting ? (
                                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white" />
                                            ) : (
                                                <>
                                                    <span className="material-symbols-outlined text-[20px]">send</span>
                                                    Envoyer le message
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Modal Image Zoom */}
            {zoomedImage && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm cursor-zoom-out animate-in fade-in duration-200"
                    onClick={() => setZoomedImage(null)}
                >
                    <div className="relative max-w-7xl max-h-[90vh] w-full flex items-center justify-center">
                        <img
                            src={zoomedImage}
                            alt="Aperçu Zoomé"
                            className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl cursor-default"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <button
                            className="absolute top-4 right-4 bg-slate-800/50 hover:bg-slate-800 text-white rounded-full p-2 transition-colors cursor-pointer"
                            onClick={() => setZoomedImage(null)}
                            title="Fermer"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HelpSupport;
